import * as Sentry from '@sentry/react';
import type { Dispatch } from '@reduxjs/toolkit';

import type {
  OfflineModeActivation,
  OfflineModeActor,
  OfflineModeDirection,
  OfflineModeDivergence,
} from './index';
import TCClient from 'client';
import { SET_TOAST_DURATION_IN_MILLISECONDS } from 'admin/utils/setToastDuration';
import { WRITE_ERROR } from 'admin/local-console/pages/OfflineMode/copy';
import { actions, store, thunk } from 'redux-stores';
import { applyState, fromWire } from 'common/utils/activationStateCache';
import { localServerRequestOptions } from 'common/utils/offlineMode/localServerAuthHeaders';

type ActivationWire = {
  org_token?: string;
  state?: string;
  version?: number;
  origin?: string;
  updated_at?: number;
  actor?: { token?: string; name?: string } | null;
  published_version?: number;
  published_at?: number;
  divergence?: DivergenceWire | null;
};

type DivergenceWire = {
  reason?: unknown;
  detected_at?: unknown;
  last_seen_at?: unknown;
  peer?: { version?: unknown; state?: unknown; epoch?: unknown } | null;
};

type EventWire = {
  event_type?: string;
  occurred_at?: number;
};

type ServerReply = { error?: { message?: unknown } };

type HttpError = {
  code?: unknown;
  status?: number;
  text?: unknown;
  body?: ServerReply;
  response?: { status?: number; body?: ServerReply };
};

const PERMISSION_DENIED = 'permission-denied';

const RETRYABLE_SWITCH_REFUSALS = [
  'offline_mode_state_unreadable',
  'offline_mode_cache_unwritable',
  'durability_lost',
];

export const LAST_ACTIVATION_PAGE_SIZE = 100;

const activationPath = (organizationId: string) =>
  `/v2/organization/${organizationId}/offline-mode`;

const holdActivation = (organizationId: string, wire: unknown): void => {
  const incoming = fromWire(organizationId, wire);
  if (incoming) applyState(incoming);
};

const httpErrorOf = (err: unknown): HttpError => (err ?? {}) as HttpError;

const nonEmptyText = (value: unknown): string | undefined =>
  typeof value === 'string' && value ? value : undefined;

const isPermissionDeniedError = (err: unknown): boolean =>
  httpErrorOf(err).code === PERMISSION_DENIED;

export function isDeploymentUnreachableError(err: unknown): boolean {
  if (isPermissionDeniedError(err)) return false;
  const error = httpErrorOf(err);
  const status = error.response?.status ?? error.status;
  return !error.response || !status;
}

export const serverMessageOf = (err: unknown): string | undefined => {
  const error = httpErrorOf(err);
  return nonEmptyText(error.text) ?? nonEmptyText(error.body?.error?.message);
};

export const statusOf = (err: unknown): number | undefined => {
  if (isPermissionDeniedError(err)) return 403;
  const error = httpErrorOf(err);
  return error.response?.status ?? error.status;
};

const isRetryableSwitchRefusal = (message: string | undefined): boolean =>
  !!message && RETRYABLE_SWITCH_REFUSALS.includes(message.split(':', 1)[0].trim());

export function parseActor(raw: ActivationWire['actor']): OfflineModeActor | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const token = typeof raw.token === 'string' ? raw.token.trim() : '';
  if (!token) return undefined;
  const name = typeof raw.name === 'string' ? raw.name.trim() : '';
  return name ? { token, name } : { token };
}

const finiteNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const wireText = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined;

function parseDivergence(
  raw: DivergenceWire | null | undefined
): OfflineModeDivergence | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const reason = wireText(raw.reason);
  if (!reason) return undefined;

  const divergence: OfflineModeDivergence = { reason };
  const detectedAt = finiteNumber(raw.detected_at);
  if (detectedAt !== undefined) divergence.detectedAt = detectedAt;
  const lastSeenAt = finiteNumber(raw.last_seen_at);
  if (lastSeenAt !== undefined) divergence.lastSeenAt = lastSeenAt;

  if (raw.peer && typeof raw.peer === 'object') {
    const peer: NonNullable<OfflineModeDivergence['peer']> = {};
    const version = finiteNumber(raw.peer.version);
    if (version !== undefined) peer.version = version;
    const state = wireText(raw.peer.state);
    if (state) peer.state = state;
    const epoch = wireText(raw.peer.epoch);
    if (epoch) peer.epoch = epoch;
    divergence.peer = peer;
  }

  return divergence;
}

export function parseActivation(
  raw: unknown,
  organizationId: string
): OfflineModeActivation | null {
  if (!raw || typeof raw !== 'object') return null;
  const wire = raw as ActivationWire;
  if (wire.state !== 'on' && wire.state !== 'off') return null;

  const activation: OfflineModeActivation = {
    orgToken: wire.org_token || organizationId,
    state: wire.state,
    version: typeof wire.version === 'number' ? wire.version : 0,
    origin: wire.origin || '',
    updatedAt: typeof wire.updated_at === 'number' ? wire.updated_at : 0,
  };

  const actor = parseActor(wire.actor);
  if (actor) activation.actor = actor;
  if (typeof wire.published_version === 'number') {
    activation.publishedVersion = wire.published_version;
  }
  if (typeof wire.published_at === 'number') activation.publishedAt = wire.published_at;
  const divergence = parseDivergence(wire.divergence);
  if (divergence) activation.divergence = divergence;

  return activation;
}

export function deriveLastActivation(
  events: unknown
): { startedAt: number; endedAt: number } | null {
  if (!Array.isArray(events)) return null;

  let endedAt: number | null = null;
  for (const entry of events as EventWire[]) {
    const occurredAt = typeof entry?.occurred_at === 'number' ? entry.occurred_at : null;
    if (occurredAt === null) continue;

    if (endedAt === null) {
      if (entry.event_type === 'deactivation') endedAt = occurredAt;
      continue;
    }
    if (entry.event_type === 'activation') {
      return occurredAt <= endedAt ? { startedAt: occurredAt, endedAt } : null;
    }
  }

  return null;
}

export const fetchOfflineModeActivation = async (dispatch: Dispatch, organizationId: string) => {
  if (!organizationId) return;

  dispatch(actions.setOfflineModeLoading(organizationId));

  try {
    const res = await TCClient.httpClient.get(
      activationPath(organizationId),
      localServerRequestOptions(TCClient.httpClient)
    );
    const activation = parseActivation(res.data, organizationId);

    if (!activation) {
      Sentry.logger.error(
        Sentry.logger.fmt`[OfflineMode] Unrecognised activation record for ${organizationId}`
      );
      dispatch(actions.setOfflineModeLoadFailed(organizationId));
      dispatch(actions.setOfflineModeDeploymentUnreachable(false, organizationId));
      return;
    }

    holdActivation(organizationId, res.data);
    dispatch(actions.setOfflineModeActivation(activation, organizationId));
  } catch (err: unknown) {
    Sentry.logger.error(Sentry.logger.fmt`[OfflineMode] Failed to read activation state`, { err });
    dispatch(actions.setOfflineModeLoadFailed(organizationId));
    dispatch(
      actions.setOfflineModeDeploymentUnreachable(isDeploymentUnreachableError(err), organizationId)
    );
  }
};

export const fetchOfflineModeLastActivation = async (
  dispatch: Dispatch,
  organizationId: string
) => {
  if (!organizationId) return;

  dispatch(actions.setOfflineModeLastActivationStatus('loading', organizationId));

  try {
    const res = await TCClient.httpClient.get(
      `${activationPath(organizationId)}/events?size=${LAST_ACTIVATION_PAGE_SIZE}`,
      localServerRequestOptions(TCClient.httpClient)
    );
    dispatch(
      actions.setOfflineModeLastActivation(deriveLastActivation(res.data?.events), organizationId)
    );
  } catch (err: unknown) {
    Sentry.logger.error(Sentry.logger.fmt`[OfflineMode] Failed to read the event log`, { err });
    dispatch(actions.setOfflineModeLastActivationStatus('error', organizationId));
  }
};

export function forbiddenMessage(isGrandfathered: boolean, serverMessage?: string): string {
  if (isGrandfathered) {
    return (
      'This organization now requires the dedicated Offline Mode activation permission. Your ' +
      'access came from being an organization administrator, which this organization no longer ' +
      'accepts for Offline Mode. Ask for the offline_mode_activation permission to be granted to ' +
      'your account on this deployment.'
    );
  }
  return (
    serverMessage || 'You do not have permission to change Offline Mode for this organization.'
  );
}

export type SwitchOfflineModeOptions = {
  organizationId: string;
  direction: OfflineModeDirection;
  isGrandfathered?: boolean;
  onSuccess?: () => void;
};

export const switchOfflineMode = async (
  dispatch: Dispatch,
  { organizationId, direction, isGrandfathered = false, onSuccess }: SwitchOfflineModeOptions
) => {
  if (!organizationId) return;
  if (store.getState().offlineMode.isSwitching) return;

  dispatch(actions.clearOfflineModeWriteError());
  dispatch(actions.setOfflineModeSwitching(true));

  try {
    const res = await TCClient.httpClient.put(activationPath(organizationId), {
      data: { state: direction === 'activate' ? 'on' : 'off' },
      ...localServerRequestOptions(TCClient.httpClient),
    });

    const activation = parseActivation(res.data, organizationId);
    if (!activation) {
      throw new Error('unrecognised activation record in switch reply');
    }

    holdActivation(organizationId, res.data);
    dispatch(actions.setOfflineModeActivation(activation, organizationId));
    thunk.setToastSettings({
      duration: SET_TOAST_DURATION_IN_MILLISECONDS(3),
      open: true,
      message:
        direction === 'activate'
          ? 'Offline Mode is now active.'
          : 'The organization has returned to Cloud Mode.',
      type: 'SUCCESS',
    });
    onSuccess?.();
  } catch (err: unknown) {
    Sentry.logger.error(Sentry.logger.fmt`[OfflineMode] Failed to switch offline mode`, { err });

    const status = statusOf(err);
    const unreachable = isDeploymentUnreachableError(err);
    let message: string;

    if (unreachable) {
      message =
        'The local server did not respond, so nothing was changed. Check that you are on the ' +
        'hospital network and try again.';
    } else if (status === 403) {
      message = forbiddenMessage(isGrandfathered, serverMessageOf(err));
    } else if (status === 409) {
      message =
        'Another administrator is changing Offline Mode for this organization right now. ' +
        'Nothing was changed. Reload the panel to see the current state before trying again.';
    } else if (isRetryableSwitchRefusal(serverMessageOf(err))) {
      message = WRITE_ERROR.notSwitched;
    } else {
      message =
        serverMessageOf(err) ||
        'The local server could not record the change, so nothing was changed.';
    }

    dispatch(actions.setOfflineModeWriteError({ message, direction }));
    thunk.setToastSettings({
      duration: SET_TOAST_DURATION_IN_MILLISECONDS(6),
      open: true,
      message:
        direction === 'activate'
          ? 'Offline Mode was not activated.'
          : 'The organization was not returned to Cloud Mode.',
      type: 'FAILURE',
    });
  } finally {
    dispatch(actions.setOfflineModeSwitching(false));
  }
};
